import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Row, Col, Button, Spin, Alert, Typography, Tag, Divider, } from 'antd';
import { LeftOutlined, UserOutlined, AppstoreOutlined, CheckCircleOutlined, CloseCircleOutlined, } from '@ant-design/icons';
import { getClientSubscriptionById } from '../../services/client-subscription';
import '../../styles/global.css';
import { List } from 'antd';

const ClientSubscriptionViewPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { Title } = Typography;
    const { Text } = Typography;

    const [loading, setLoading] = useState(true);
    const [subscription, setSubscription] = useState(null);
    const [error, setError] = useState(null);

    const statusMap = {
        PENDING: {
        label: 'Pendiente',
        color: '#BA955E',
        },
        ACTIVE: {
        label: 'Activa',
        color: 'green',
        },
        CANCELED: {
        label: 'Cancelada',
        color: 'red',
        },
        COMPLETED: {
        label: 'Completada',
        color: 'blue',
        },
        FAILED: {
        label: 'Completada',
        color: 'volcano',
        },
    };

    useEffect(() => {
        if (!id) {
        setError('ID de suscripción no proporcionado');
        setLoading(false);
        return;
        }

        const loadSubscription = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await getClientSubscriptionById(id);
            setSubscription(data);
        } catch (err) {
            console.error(err);
            setError('No se pudo cargar la información de la suscripción');
        } finally {
            setLoading(false);
        }
        };

        loadSubscription();
    }, [id]);

    const formatCurrency = (amount, currency = 'MXN') => {
        if (amount == null) return '—';

        return new Intl.NumberFormat('es-MX', {
            style: 'currency',
            currency,
        }).format(amount);
    };


    const handleBack = () => {
        navigate('/subscriptions/all');
    };

    if (loading) {
        return (
        <div style={{ padding: 24, display: 'flex', justifyContent: 'center', minHeight: 400 }}>
            <Spin size="large" />
        </div>
        );
    }

    if (error) {
        return (
        <div style={{ padding: 24 }}>
            <Alert
            message="Error"
            description={error}
            type="error"
            showIcon
            action={<Button onClick={handleBack}>Volver</Button>}
            />
        </div>
        );
    }

    if (!subscription) {
        return (
        <div style={{ padding: 24 }}>
            <Alert
            message="Suscripción no encontrada"
            type="warning"
            showIcon
            action={<Button onClick={handleBack}>Volver</Button>}
            />
        </div>
        );
    }

    const statusInfo = statusMap[subscription.status] || {
        label: subscription.status,
        color: 'default',
    };

    return (
        <div style={{ padding: 24, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Button
            type="text"
            icon={<LeftOutlined style={{ fontSize: 24 }} />}
            onClick={handleBack}
            />
            <Title level={3} style={{ marginBottom: 0 }}>
                Detalles de Suscripción
            </Title>
        </div>

        <Card style={{ borderRadius: 32 }}>
            {/* ESTADO */}
            <Row gutter={16}>
            <Col span={24}>
                <Tag color={statusInfo.color}>
                    {statusInfo.label}
                </Tag>
                {subscription.deleted && (
                <Tag color="red">Eliminada</Tag>
                )}
            </Col>
            </Row>

            <Divider />

            {/* INFO SUSCRIPCIÓN */}
            <Row gutter={16}>
            <Col xs={24} md={8} className="col-spaced">
                <div className="field-label">Fecha inicio</div>
                <div className="field-value">
                {new Date(subscription.startDate).toLocaleDateString()}
                </div>
            </Col>

            <Col xs={24} md={8} className="col-spaced">
                <div className="field-label">Fecha fin</div>
                <div className="field-value">
                {new Date(subscription.endDate).toLocaleDateString()}
                </div>
            </Col>

            <Col xs={24} md={8} className="col-spaced">
                <div className="field-label">Medio</div>
                <div className="field-value">
                {subscription.medium}
                </div>
            </Col>

            <Col xs={24} md={8} className="col-spaced">
                <div className="field-label">Precio</div>
                <div className="field-value">
                {formatCurrency(subscription.price, subscription.currency)}
                </div>
            </Col>

            <Col xs={24} md={8} className="col-spaced">
                <div className="field-label">Total</div>
                <div className="field-value">
                {formatCurrency(subscription.total, subscription.currency)}
                </div>
            </Col>
            </Row>

            <Divider orientation="left">
            <UserOutlined /> Cliente
            </Divider>

            {/* CLIENTE */}
            <Row gutter={16}>
            <Col xs={24} md={8} className="col-spaced">
                <div className="field-label">Nombre</div>
                <div className="field-value">
                {`${subscription.client?.firstName} ${subscription.client?.lastName} ${subscription.client?.secondLastName || ''}` || '-'}
                </div>
            </Col>

            <Col xs={24} md={8} className="col-spaced">
                <div className="field-label">Correo</div>
                <div className="field-value">
                {subscription.client?.email || '-'}
                </div>
            </Col>
            </Row>

            <Divider orientation="left">
            <AppstoreOutlined /> Plan
            </Divider>

            {/* PLAN */}
            <Row gutter={16}>
            <Col xs={24} md={6} className="col-spaced">
                <div className="field-label">Código</div>
                <div className="field-value">
                {subscription.plan?.code}
                </div>
            </Col>

            <Col xs={24} md={6} className="col-spaced">
                <div className="field-label">Nombre</div>
                <div className="field-value">
                {subscription.plan?.name}
                </div>
            </Col>

            <Col xs={24} md={6} className="col-spaced">
                <div className="field-label">Precio</div>
                <div className="field-value">
                {formatCurrency(subscription.plan?.price, subscription.plan?.currency)} {subscription.plan?.currency}
                </div>
            </Col>

            <Col xs={24} md={6} className="col-spaced">
                <div className="field-label">Duración</div>
                <div className="field-value">
                {subscription.plan?.durationDays} días
                </div>
            </Col>
            </Row>
            <Card
                title="Beneficios del plan"
                style={{
                    marginTop: 24,
                    borderRadius: '24px',
                }}
                >
                <List
                    dataSource={Object.entries(subscription.plan?.benefits || {})}
                    locale={{ emptyText: 'Este plan no tiene beneficios definidos' }}
                    renderItem={([key, benefit]) => (
                    <List.Item>
                        <List.Item.Meta
                            avatar={
                                benefit.can ? (
                                <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 18 }} />
                                ) : (
                                <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: 18 }} />
                                )
                            }
                            title={
                                <Text strong>
                                {benefit.label || key}
                                </Text>
                            }
                            description={
                                !benefit.can ? (
                                    <Tag color="red">No permitido</Tag>
                                ) : benefit.limit != null ? (
                                    <Tag color="blue">Permitido · Límite: {benefit.limit}</Tag>
                                ) : (
                                    <Tag color="green">Permitido · Ilimitado</Tag>
                                )
                            }
                        />
                    </List.Item>
                    )}
                />
                </Card>

        </Card>
        </div>
    );
};

export default ClientSubscriptionViewPage;
